import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  KeyRound, CheckCircle2, Loader2, Send, ShieldCheck, Clock, Home,
} from "lucide-react";

/** 14-day plot lock — no money required. Contact-only reservation. */
export default function ReservePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", country: "",
    plot_pref: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { reservation_id, expires_at }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/projects/${id}`);
        setProject(data);
      } catch {
        toast.error("Project not found.");
        nav("/projects", { replace: true });
      }
    })();
    // Prefill from signed-in user
    if (user) setForm((f) => ({
      ...f,
      full_name: user.name || f.full_name,
      email:     user.email || f.email,
      phone:     user.phone || f.phone,
    }));
  }, [id, nav, user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) { toast.error("Name and email are required."); return; }
    setBusy(true);
    try {
      let ref = "";
      try { ref = localStorage.getItem("mv_ref") || ""; } catch {}
      const { data } = await api.post("/reservations", {
        project_id: project.id, ...form, ref_code: ref,
      });
      setDone({ reservation_id: data.reservation_id, expires_at: data.expires_at });
      toast.success("Plot reserved for 14 days.");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (!project) return <main className="min-h-[50vh] flex items-center justify-center"><Loader2 className="animate-spin mv-green-text"/></main>;

  return (
    <main data-testid="reserve-page">
      <section className="mv-green-bg text-white py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ Reserve your plot</div>
          <h1 className="font-display text-3xl md:text-5xl mt-4 leading-[1.02]">
            {project.name}
          </h1>
          <p className="mt-3 text-white/85">
            {project.city}, {project.country_flag} {project.country} · from {new Intl.NumberFormat("en-GB", { style: "currency", currency: (project.currency || "GBP").toUpperCase(), maximumFractionDigits: 0 }).format(project.price_from || 0)}
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 md:px-8 py-14 md:py-20">
        {done ? (
          <div className="mv-card p-8" data-testid="reserve-success" style={{ background: "linear-gradient(180deg, #fff, rgba(37,211,102,0.06))" }}>
            <div className="flex items-start gap-3">
              <CheckCircle2 size={26} style={{ color: "#0A7A2C" }} className="mt-1"/>
              <div>
                <div className="font-display text-2xl mv-green-text">Reserved for 14 days.</div>
                <p className="mt-2 text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
                  Reservation ID: <span className="font-mono">{done.reservation_id}</span><br/>
                  Held until: <strong>{new Date(done.expires_at).toLocaleString()}</strong>
                </p>
                <p className="mt-3 text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
                  <strong>No money required today.</strong> Our team will contact you within 1 working day to walk through your contract, verify citizenship, and send you the sample deed. You can pay your 15% deposit any time before the lock expires — or cancel with no penalty.
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link to="/pay?type=deposit" className="btn-gold" data-testid="reserve-pay-deposit">Pay 15% deposit now</Link>
                  <Link to="/how-your-money-is-held" className="btn-ghost" data-testid="reserve-see-money">See how your money is held</Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="mv-card p-6 mb-6" data-testid="reserve-perks" style={{ background: "linear-gradient(180deg, rgba(212,175,55,0.08), #fff)" }}>
              <ul className="text-[14px] space-y-2" style={{ color: "var(--mv-ink)" }}>
                <li className="flex items-start gap-2"><Clock size={15} className="mt-0.5 mv-green-text"/>14-day exclusive hold on your preferred plot — nobody else can reserve it.</li>
                <li className="flex items-start gap-2"><ShieldCheck size={15} className="mt-0.5 mv-green-text"/>No money required today. Full refund if you cancel before signing.</li>
                <li className="flex items-start gap-2"><KeyRound size={15} className="mt-0.5 mv-green-text"/>Title deed will be registered in your name once fully paid.</li>
              </ul>
            </div>

            <form onSubmit={submit} className="mv-card p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="reserve-form">
              <F label="Full name *"          v={form.full_name} on={set("full_name")} tid="rv-name"/>
              <F label="Email *"              v={form.email}     on={set("email")}     type="email" tid="rv-email"/>
              <F label="Phone / WhatsApp"     v={form.phone}     on={set("phone")}     tid="rv-phone"/>
              <F label="Country you live in"  v={form.country}   on={set("country")}   tid="rv-country"/>
              <F label="Preferred plot / type"v={form.plot_pref} on={set("plot_pref")} placeholder="e.g. Corner plot · 3-bed · south-facing" className="md:col-span-2" tid="rv-plot"/>
              <label className="block md:col-span-2">
                <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Anything we should know</span>
                <textarea value={form.notes} onChange={set("notes")} rows={3}
                          className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]" data-testid="rv-notes"/>
              </label>
              <div className="md:col-span-2 pt-3 border-t mv-hairline flex justify-between items-center">
                <div className="text-[12.5px] font-accent italic" style={{ color: "var(--mv-muted)" }}>
                  No card required. No credit checks.
                </div>
                <button type="submit" disabled={busy} className="btn-green" data-testid="rv-submit">
                  {busy ? <Loader2 className="animate-spin" size={16}/> : <Home size={16}/>}
                  {busy ? "Reserving…" : "Reserve this plot (14 days)"}
                </button>
              </div>
            </form>
          </>
        )}
      </section>
    </main>
  );
}

function F({ label, v, on, type = "text", placeholder, className = "", tid }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>{label}</span>
      <input value={v} onChange={on} type={type} placeholder={placeholder}
             className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]" data-testid={tid}/>
    </label>
  );
}
